/**
 * Fetches the brand-theme config from `GET /extranet/config` and applies its
 * CSS custom properties on `:root`, so every Tailwind utility of the module
 * picks up the tenant's branding. Copied from claimflow's `routes.tsx`.
 *
 * Runs once per mount. The route is mounted as `/extranet/:tenantSlug/*`, so
 * the slug is passed explicitly to `getConfig` — the anonymous (pre-login)
 * fetch resolves the tenant from the URL rather than a stale stored tenant.
 * A failure (incl. 404 for an unknown slug) keeps the default theme.
 */

import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { ThemeOverrideConfig } from '@components/integrated/theme-override';
import { extranetAuthService } from '../api/authService';

const RADIUS_VARS: Record<string, string> = {
  sm: '--radius-sm',
  DEFAULT: '--radius',
  md: '--radius-md',
  lg: '--radius-lg',
  xl: '--radius-xl',
  '2xl': '--radius-2xl',
};

const SHADOW_VARS: Record<string, string> = {
  sm: '--shadow-sm',
  DEFAULT: '--shadow',
  md: '--shadow-md',
  lg: '--shadow-lg',
  xl: '--shadow-xl',
};

/** Write a brand theme's tokens as CSS custom properties on `root`. */
export function applyBrandTheme(theme: ThemeOverrideConfig, root: HTMLElement): void {
  if (theme.colors) {
    const palettes = Object.entries(theme.colors) as [
      string,
      Partial<Record<string, string>> | undefined,
    ][];
    for (const [palette, scale] of palettes) {
      if (!scale) continue;
      for (const [shade, value] of Object.entries(scale)) {
        if (typeof value === 'string') {
          root.style.setProperty(`--color-${palette}-${shade}`, value);
        }
      }
    }
  }

  if (theme.typography?.fontSans) {
    root.style.setProperty('--font-sans', theme.typography.fontSans);
  }
  if (theme.typography?.fontMono) {
    root.style.setProperty('--font-mono', theme.typography.fontMono);
  }

  for (const [key, value] of Object.entries(theme.borderRadius ?? {})) {
    const cssVar = RADIUS_VARS[key];
    if (cssVar && typeof value === 'string') root.style.setProperty(cssVar, value);
  }

  for (const [key, value] of Object.entries(theme.shadows ?? {})) {
    const cssVar = SHADOW_VARS[key];
    if (cssVar && typeof value === 'string') root.style.setProperty(cssVar, value);
  }
}

export function useApplyBrandTheme(): void {
  const applied = useRef(false);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  useEffect(() => {
    if (applied.current) return;
    applied.current = true;

    extranetAuthService
      .getConfig(tenantSlug)
      .then((res) => {
        const theme = res.data?.config?.['brand-theme'] as ThemeOverrideConfig | undefined;
        if (theme) applyBrandTheme(theme, document.documentElement);
      })
      .catch(() => {
        // Config fetch failure should not block the UI — default theme.
      });
    // `applied` guards run-once; tenantSlug is stable for a given route.
  }, [tenantSlug]);
}
